import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faLink } from '@fortawesome/free-solid-svg-icons';
import {
    getServiceProfessionals,
    getPublicServices,
    ServiceProfessional,
    PublicServiceLite,
} from '../../../../service/discovery/discovery-service';
import './ShareBookingModal.css';

export interface ShareBookingModalProps {
    open: boolean;
    onClose: () => void;
    slug?: string | null;
    services: { id: string; name: string }[];
    initialServiceId?: string;
    /** When provided, pre-selects the professional whose name matches (case-insensitive). */
    initialMemberName?: string;
    /** When provided (opened from the Team screen), the modal runs PROFESSIONAL-first:
     *  the professional combo is shown first and pre-selected to this member, and the
     *  service combo lists only the services they perform. */
    initialMemberServiceIds?: string[];
}

/**
 * Resolve the consumer-web base for share links from REACT_APP_PUBLIC_WEB_BASE_URL,
 * supporting three shapes so the link is correct in every environment:
 *  - absolute URL  ("http://localhost:3000", "https://getservice.io") → used as-is
 *  - relative path ("/client") → resolved against the CURRENT origin, so behind
 *    ngrok or a local nginx the link points at the host actually serving the admin
 *    (no rebuild, no hardcoded getservice.io) — mirrors how the API base URLs are set
 *  - empty → the current origin (admin and consumer share the host)
 */
function resolveConsumerBase(): string {
    const cfg = (process.env.REACT_APP_PUBLIC_WEB_BASE_URL || '').trim().replace(/\/$/, '');
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    if (!cfg) return origin;
    if (/^https?:\/\//i.test(cfg)) return cfg;
    return `${origin}${cfg.startsWith('/') ? cfg : '/' + cfg}`;
}

const ShareBookingModal: React.FC<ShareBookingModalProps> = ({
    open,
    onClose,
    slug,
    services,
    initialServiceId,
    initialMemberName,
    initialMemberServiceIds,
}) => {
    const { t } = useTranslation();

    // Professional-first mode (opened from the Team screen): we have the member's
    // service ids, so the service combo is scoped to them and the default service
    // is one they actually perform — which is what makes the by-name professional
    // auto-select land (the member only appears in serviceProfessionals of services
    // they do).
    const memberServiceIdSet = new Set(initialMemberServiceIds ?? []);
    const memberFirst = memberServiceIdSet.size > 0;

    /** Best default service for a given list: in member-first mode the member's
     *  first service; otherwise the explicit initialServiceId, else the first. */
    const pickDefaultService = (list: { id: string }[]): string => {
        if (memberFirst) {
            const own = list.find((s) => memberServiceIdSet.has(s.id));
            if (own) return own.id;
        }
        if (initialServiceId && list.some((s) => s.id === initialServiceId)) return initialServiceId;
        return list[0]?.id ?? '';
    };

    const [selectedServiceId, setSelectedServiceId] = useState(() => pickDefaultService(services));
    // Source of truth for the dropdown = services the consumer can actually book
    // (from discovery). Falls back to the passed-in admin list until it loads.
    const [publicServices, setPublicServices] = useState<PublicServiceLite[]>([]);
    const [loadingServices, setLoadingServices] = useState(false);
    const [professionals, setProfessionals] = useState<ServiceProfessional[]>([]);
    const [selectedMember, setSelectedMember] = useState('');
    const [loadingPros, setLoadingPros] = useState(false);

    const effectiveServices: { id: string; name: string }[] =
        publicServices.length > 0 ? publicServices : services;
    // In professional-first mode, only offer the services this member performs.
    const dropdownServices: { id: string; name: string }[] = memberFirst
        ? effectiveServices.filter((s) => memberServiceIdSet.has(s.id))
        : effectiveServices;
    /** Per-link copy state: 'full' | 'short' | null */
    const [copiedKey, setCopiedKey] = useState<'full' | 'short' | null>(null);

    const fullLinkInputRef = useRef<HTMLInputElement>(null);
    const shortLinkInputRef = useRef<HTMLInputElement>(null);

    // Re-initialise when the modal OPENS (or the pre-selected service changes).
    // Deliberately NOT keyed on `services`/`initialMemberServiceIds`: both callers
    // pass a fresh array reference on every render (`services={services.map(...)}`),
    // so including them would re-run this — and wipe the professional the user
    // picked — on any parent re-render, e.g. when the business list re-validates on
    // window focus after clicking "Open". The authoritative reconciliation against
    // the publicly-bookable services happens in the effect below.
    useEffect(() => {
        if (!open) return;
        setSelectedServiceId(pickDefaultService(services));
        setSelectedMember('');
        setProfessionals([]);
        setCopiedKey(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialServiceId]);

    // Load the publicly bookable services (the dropdown source) when the modal
    // opens. Reconcile the current selection against this authoritative list so
    // we never build a link for a service the consumer can't actually book.
    useEffect(() => {
        if (!open || !slug) {
            setPublicServices([]);
            return;
        }
        let cancelled = false;
        setLoadingServices(true);
        getPublicServices(slug).then((list) => {
            if (cancelled) return;
            setPublicServices(list);
            setLoadingServices(false);
            if (list.length > 0) {
                // Reconcile against the authoritative public list. In member-first
                // mode prefer one of the member's services so the professional
                // auto-select can resolve.
                const scoped = memberFirst ? list.filter((s) => memberServiceIdSet.has(s.id)) : list;
                const wanted = pickDefaultService(scoped.length > 0 ? scoped : list);
                setSelectedServiceId((cur) =>
                    (memberFirst ? scoped : list).some((s) => s.id === cur) ? cur : wanted,
                );
            }
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, slug, initialServiceId]);

    // Load professionals whenever the service selection changes.
    useEffect(() => {
        if (!open || !slug || !selectedServiceId) {
            setProfessionals([]);
            return;
        }
        let cancelled = false;
        setLoadingPros(true);
        setSelectedMember('');
        getServiceProfessionals(slug, selectedServiceId).then((list) => {
            if (!cancelled) {
                setProfessionals(list);
                setLoadingPros(false);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [open, slug, selectedServiceId]);

    // Auto-select professional by name once the list loads.
    useEffect(() => {
        if (!initialMemberName || professionals.length === 0) return;
        const needle = initialMemberName.trim().toLowerCase();
        const match = professionals.find((p) => p.name.trim().toLowerCase() === needle);
        if (match) setSelectedMember(match.id);
    }, [professionals, initialMemberName]);

    // Close on Escape.
    useEffect(() => {
        if (!open) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open, onClose]);

    if (!open) return null;

    const publicWebBase = resolveConsumerBase();
    const memberSuffix = selectedMember ? `&member=${selectedMember}` : '';
    const fullLink = slug && selectedServiceId
        ? `${publicWebBase}/b/${slug}?service=${selectedServiceId}${memberSuffix}`
        : '';
    const shortLink = slug && selectedServiceId
        ? `${publicWebBase}/b/${slug}/quick?service=${selectedServiceId}${memberSuffix}`
        : '';

    const copyLink = async (text: string, key: 'full' | 'short', inputRef: React.RefObject<HTMLInputElement>) => {
        if (!text) return;
        const flash = () => {
            setCopiedKey(key);
            setTimeout(() => setCopiedKey(null), 2000);
        };
        try {
            await navigator.clipboard.writeText(text);
            flash();
        } catch {
            if (inputRef.current) {
                inputRef.current.select();
                try { document.execCommand('copy'); flash(); } catch { /* nothing */ }
            }
        }
    };

    // The two selectors as elements so we can flip their order: service-first
    // (opened from Services) vs professional-first (opened from a Team member).
    const serviceField = (
        <div className="sbm__field" key="sbm-service-field">
            <label className="sbm__label" htmlFor="sbm-service">
                {t('share.service')}
            </label>
            <select
                id="sbm-service"
                className="form-select form-select-sm"
                value={selectedServiceId}
                onChange={(e) => setSelectedServiceId(e.target.value)}
                disabled={loadingServices}
                data-cy="share-booking-service-select"
            >
                {dropdownServices.map((s) => (
                    <option key={s.id} value={s.id}>
                        {s.name}
                    </option>
                ))}
            </select>
            {!loadingServices && slug && dropdownServices.length === 0 && (
                <p className="sbm__link-hint">{t('share.noServices')}</p>
            )}
        </div>
    );

    const professionalField = (
        <div className="sbm__field" key="sbm-professional-field">
            <label className="sbm__label" htmlFor="sbm-professional">
                {t('share.professional')}
                {loadingPros && (
                    <span className="sbm__loading-badge">{t('common.loading')}</span>
                )}
            </label>
            <select
                id="sbm-professional"
                className="form-select form-select-sm"
                value={selectedMember}
                onChange={(e) => setSelectedMember(e.target.value)}
                disabled={loadingPros}
                data-cy="share-booking-professional-select"
            >
                <option value="">{t('share.anyProfessional')}</option>
                {professionals.map((p) => (
                    <option key={p.id} value={p.id}>
                        {p.name}
                    </option>
                ))}
            </select>
        </div>
    );

    const modal = (
        <div
            className="sbm__overlay"
            onMouseDown={onClose}
            role="presentation"
        >
            <div
                className="sbm__modal"
                role="dialog"
                aria-modal="true"
                aria-label={t('share.title')}
                onMouseDown={(e) => e.stopPropagation()}
                data-cy="share-booking-modal"
            >
                {/* Header */}
                <div className="sbm__head">
                    <div className="sbm__title">
                        <FontAwesomeIcon icon={faLink} className="sbm__title-icon" />
                        {t('share.title')}
                    </div>
                    <button
                        type="button"
                        className="sbm__close"
                        onClick={onClose}
                        aria-label={t('common.close')}
                        data-cy="share-booking-close"
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>

                {/* Body */}
                <div className="sbm__body">
                    {!slug ? (
                        <p className="sbm__no-slug">{t('share.noSlug')}</p>
                    ) : (
                        <>
                            {/* Service-first (from Services) vs professional-first
                                (from a Team member): flip the two selectors' order. */}
                            {memberFirst
                                ? [professionalField, serviceField]
                                : [serviceField, professionalField]}

                            {/* Full link */}
                            <div className="sbm__field sbm__link-section">
                                <div className="sbm__link-section-head">
                                    <span className="sbm__label">{t('share.fullLink')}</span>
                                    <span className="sbm__link-hint">{t('share.fullLinkHint')}</span>
                                </div>
                                <div className="sbm__link-row">
                                    <input
                                        id="sbm-link-full"
                                        ref={fullLinkInputRef}
                                        type="text"
                                        className="form-control form-control-sm sbm__link-input"
                                        readOnly
                                        value={fullLink}
                                        data-cy="share-booking-full-link-input"
                                        onFocus={(e) => e.target.select()}
                                    />
                                    <button
                                        type="button"
                                        className={`btn btn-sm sbm__copy-btn ${copiedKey === 'full' ? 'btn-success' : 'btn-outline-primary'}`}
                                        onClick={() => copyLink(fullLink, 'full', fullLinkInputRef)}
                                        disabled={!fullLink}
                                        data-cy="share-booking-full-copy-btn"
                                    >
                                        {copiedKey === 'full' ? t('share.copied') : t('share.copy')}
                                    </button>
                                    <a
                                        href={fullLink || '#'}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="sbm__open-link"
                                        tabIndex={fullLink ? 0 : -1}
                                        aria-label={t('share.open')}
                                        title={t('share.open')}
                                        data-cy="share-booking-full-open-link"
                                    >
                                        {t('share.open')}
                                    </a>
                                </div>
                            </div>

                            {/* Short / compact link */}
                            <div className="sbm__field sbm__link-section">
                                <div className="sbm__link-section-head">
                                    <span className="sbm__label">{t('share.shortLink')}</span>
                                    <span className="sbm__link-hint">{t('share.shortLinkHint')}</span>
                                </div>
                                <div className="sbm__link-row">
                                    <input
                                        id="sbm-link-short"
                                        ref={shortLinkInputRef}
                                        type="text"
                                        className="form-control form-control-sm sbm__link-input"
                                        readOnly
                                        value={shortLink}
                                        data-cy="share-booking-short-link-input"
                                        onFocus={(e) => e.target.select()}
                                    />
                                    <button
                                        type="button"
                                        className={`btn btn-sm sbm__copy-btn ${copiedKey === 'short' ? 'btn-success' : 'btn-outline-primary'}`}
                                        onClick={() => copyLink(shortLink, 'short', shortLinkInputRef)}
                                        disabled={!shortLink}
                                        data-cy="share-booking-short-copy-btn"
                                    >
                                        {copiedKey === 'short' ? t('share.copied') : t('share.copy')}
                                    </button>
                                    <a
                                        href={shortLink || '#'}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="sbm__open-link"
                                        tabIndex={shortLink ? 0 : -1}
                                        aria-label={t('share.open')}
                                        title={t('share.open')}
                                        data-cy="share-booking-short-open-link"
                                    >
                                        {t('share.open')}
                                    </a>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="sbm__footer">
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={onClose}
                        data-cy="share-booking-cancel-btn"
                    >
                        {t('common.close')}
                    </button>
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(modal, document.body);
};

export default ShareBookingModal;
